import React from 'react'
import Step1 from './Step1'
import { Button } from 'react-bootstrap'
import Step2 from './Step2'
import Step3 from './Step3'
import { validateAgentCode, registerUser } from '../../apis/registrationApi'

interface ErrorsType {
  agentCode: string
  name: string
  email: string
  trueInfoConsent: string
  consent: string
}

export interface FormData {
  agentCode: string
  name: string
  email: string
  trueInfoConsent: boolean
  consent: boolean
}

export default function RegisterForm() {
  const [step, setStep] = React.useState(1)
  const [loading, setLoading] = React.useState(false)
  const [errors, setErrors] = React.useState<ErrorsType>({
    agentCode: '',
    name: '',
    email: '',
    trueInfoConsent: '',
    consent: '',
  })
  const [formData, setFormData] = React.useState<FormData>({
    agentCode: '',
    name: '',
    email: '',
    trueInfoConsent: false,
    consent: false,
  })

  const handleNext = async () => {
    if (step === 1) {
      await handleValidateAgentCode()
    } else if (step === 2) {
      await handleRegisterUser()
    }
  }

  const handleValidateAgentCode = async () => {
    if (!formData.agentCode.trim()) {
      setErrors((prev) => ({ ...prev, agentCode: 'Please enter your agent code' }))
      return
    }

    setLoading(true)
    setErrors((prev) => ({ ...prev, agentCode: '' }))

    validateAgentCode(
      formData.agentCode,
      (data, status) => {
        setLoading(false)
        if (status === 200) {
          setErrors((prev) => ({ ...prev, agentCode: '' }))
          setStep(2)
        }
      },
      (error) => {
        setLoading(false)
        setErrors((prev) => ({ ...prev, agentCode: error.response?.data?.message || 'Invalid agent code. Please try again.' }))
      }
    )
  }

  const handleRegisterUser = async () => {
    // Clear previous errors
    setErrors((prev) => ({ ...prev, name: '', email: '', consent: '' }))

    let hasErrors = false

    if (!formData.name.trim()) {
      setErrors((prev) => ({ ...prev, name: 'Please enter your name' }))
      hasErrors = true
    }

    if (!formData.email.trim()) {
      setErrors((prev) => ({ ...prev, email: 'Please enter your email address' }))
      hasErrors = true
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setErrors((prev) => ({ ...prev, email: 'Please enter a valid email address' }))
      hasErrors = true
    }

    if (!formData.trueInfoConsent) {
      setErrors((prev) => ({ ...prev, trueInfoConsent: 'Please check the box to proceed' }))
      hasErrors = true
    }

    if (!formData.consent) {
      setErrors((prev) => ({ ...prev, consent: 'Please accept the consent to proceed' }))
      hasErrors = true
    }

    if (hasErrors) return

    setLoading(true)

    registerUser(
      {
        agent_code: formData.agentCode,
        name: formData.name,
        email: formData.email,
        consent: formData.consent,
      },
      (data, status) => {
        setLoading(false)
        if (data.errors) {
          throw new Error(data.errors)
        }

        if (status === 200 || status === 201) {
          setStep(3)
        }
      },
      (error) => {
        setLoading(false)
        // Handle server errors - could be field-specific or general
        const serverError = error?.response?.data?.errors || 'Registration failed. Please try again.'
        setErrors((prev) => ({ ...prev, email: serverError })) // Show server error under email field
      }
    )
  }

  const updateFormData = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    // Clear error when user starts typing
    setErrors((prev) => ({
      ...prev,
      [field]: '',
    }))
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleNext()
  }

  return (
    <>
      {step === 1 && <Step1 formData={formData} updateFormData={updateFormData} errors={errors} onSubmit={handleFormSubmit} />}
      {step === 2 && <Step2 formData={formData} updateFormData={updateFormData} errors={errors} onSubmit={handleFormSubmit} />}
      {step === 3 && <Step3 />}
      {step !== 3 && (
        <div className="text-center">
          <Button variant="primary" size="lg" onClick={handleNext} disabled={loading}>
            {loading ? 'Loading...' : 'NEXT'}
          </Button>
        </div>
      )}
    </>
  )
}
